import { useFormContext } from 'react-hook-form';
import { TextKey } from '@mews-commander/enums';
import { LocalizedSmallLabel } from '@mews-commander/ui';
import { WithAvailabilityBlockId, WithServiceId } from '@mews-commander/types';
import { getNowUtc } from '@mews-commander/utils';
import { WithFormArrayFields, WithDateTimeRangeArray, AvailabilityAdjustmentFormValues } from '../../../../types';
import ReadOnlyCell from '../Cells/VectorCells/ReadOnlyCell';
import { BlockSetupMatrixVectorTotalCell, BlockSetUpVectorCell, BlockSetUpVectorTotalCell } from '../Cells/styles';
import { BlockSetupMatrixVectorElement } from '../styles';
import { EditableCell } from '../Cells/VectorCells/EditableCell';
import TotalCell from '../Cells/VectorCells/TotalCell';
import TotalCellEditableMode from '../Cells/VectorCells/TotalCellEditableMode';
import { getAdjustmentSelector } from '../../../../selectors';
import { GuestCountCell } from '../Cells/VectorCells/GuestCountCell';

interface RenderVectorParams
    extends WithFormArrayFields,
        WithAvailabilityBlockId,
        WithDateTimeRangeArray,
        WithServiceId {
    isEditingAvailability: boolean;
    utcRangeArray: Array<string>;
    occupancyInBlocksEnabled: boolean;
    withCheckoutColumn: boolean;
    startIndex: number;
    adjustmentSelector: ReturnType<typeof getAdjustmentSelector>;
}

export const RenderVector = ({
    fields,
    utcRangeArray,
    availabilityBlockId,
    serviceId,
    isEditingAvailability,
    withCheckoutColumn,
    startIndex,
    dateTimeRangeArray,
    occupancyInBlocksEnabled,
    adjustmentSelector,
}: RenderVectorParams) => {
    const { watch } = useFormContext<AvailabilityAdjustmentFormValues>();
    const watchedFields = watch();

    const children = fields
        .map((field, rowIndex) => {
            const { resourceCategoryId } = watchedFields.fieldArrayAvailability[rowIndex];
            return (
                <BlockSetupMatrixVectorElement key={field.id}>
                    {isEditingAvailability && occupancyInBlocksEnabled && (
                        <BlockSetUpVectorCell alignment="start">
                            <GuestCountCell
                                resourceCategoryId={resourceCategoryId}
                                serviceId={serviceId}
                                rowIndex={rowIndex}
                            />
                        </BlockSetUpVectorCell>
                    )}
                    {utcRangeArray.map((dateTimeUtc, j) => {
                        const index = j + startIndex;
                        const key = `block-setup-vector-cell-${index}-${dateTimeUtc}`;

                        if (!resourceCategoryId) {
                            // return empty cell if resourceCategoryId is not defined yet
                            return (
                                <BlockSetUpVectorCell key={key} alignment="end">
                                    <></>
                                </BlockSetUpVectorCell>
                            );
                        }

                        const { disabled } = field.availabilities[index] || {};
                        const isCheckoutColumn = withCheckoutColumn && j === utcRangeArray.length - 1;

                        const releaseOverrideUtc = adjustmentSelector({
                            availabilityBlockId,
                            resourceCategoryId,
                            startUtc: dateTimeUtc,
                        })?.releaseOverrideUtc;

                        const nowUtc = getNowUtc();

                        const dataTestField = `fieldArrayAvailability.${rowIndex}.availabilities.${j}.adjustmentValue.value`;

                        const isReleaseOverriden =
                            !!releaseOverrideUtc && nowUtc < releaseOverrideUtc && !isEditingAvailability;

                        return (
                            <BlockSetUpVectorCell key={key} alignment="end" isWarningCell={isReleaseOverriden}>
                                {!isEditingAvailability || isCheckoutColumn ? (
                                    <ReadOnlyCell
                                        isCheckoutColumn={isCheckoutColumn}
                                        dateTimeUtc={dateTimeUtc}
                                        availabilityBlockId={availabilityBlockId}
                                        resourceCategoryId={resourceCategoryId}
                                        isReleaseOverriden={isReleaseOverriden}
                                        dataTestField={dataTestField}
                                    />
                                ) : (
                                    <EditableCell
                                        rowIndex={rowIndex}
                                        columnIndex={index}
                                        dateTimeUtc={dateTimeUtc}
                                        availabilityBlockId={availabilityBlockId}
                                        disabled={disabled}
                                    />
                                )}
                            </BlockSetUpVectorCell>
                        );
                    })}
                </BlockSetupMatrixVectorElement>
            );
        })
        .concat(
            <BlockSetupMatrixVectorElement key="block-setup-total-row-vector-elements">
                {isEditingAvailability && occupancyInBlocksEnabled && (
                    <BlockSetUpVectorTotalCell>
                        <></>
                    </BlockSetUpVectorTotalCell>
                )}

                {utcRangeArray.map((dateTimeUtc, j) => {
                    const index = j + startIndex;

                    if (withCheckoutColumn && index === dateTimeRangeArray.length - 1) {
                        return (
                            <BlockSetUpVectorTotalCell
                                key={`block-setup-total-row-vector-cell-${dateTimeUtc}`}
                                alignment="end"
                            >
                                <BlockSetupMatrixVectorTotalCell>
                                    <LocalizedSmallLabel textKey={TextKey.CheckOut} as="div" />
                                </BlockSetupMatrixVectorTotalCell>
                            </BlockSetUpVectorTotalCell>
                        );
                    }
                    return (
                        <BlockSetUpVectorTotalCell
                            key={`block-setup-total-row-vector-cell-${dateTimeUtc}`}
                            alignment="end"
                        >
                            {isEditingAvailability ? (
                                <TotalCellEditableMode
                                    dateTimeUtc={dateTimeUtc}
                                    availabilityBlockId={availabilityBlockId}
                                />
                            ) : (
                                <TotalCell dateTimeUtc={dateTimeUtc} availabilityBlockId={availabilityBlockId} />
                            )}
                        </BlockSetUpVectorTotalCell>
                    );
                })}
            </BlockSetupMatrixVectorElement>
        );

    return children;
};
